// ══════════════════════════════════════════════════════════════
// RBAC — Roles, Groups & Access Control Models
// Inspired by ServiceNow IAM, adapted for Befret scale
// ══════════════════════════════════════════════════════════════

// ── RbacModule type: re-exported from nav-sections (source of truth) ──
// This avoids maintaining the module list in two places.
// NAV_SECTIONS drives both sidebar rendering AND RBAC configuration.
import { NAV_SECTIONS, RbacModule } from '../layout/nav-sections';
export { RbacModule } from '../layout/nav-sections';

// ── Module display config ──

export const RBAC_MODULE_CONFIG: Record<RbacModule, { label: string; icon: string; color: string }> = {
  logistics_europe:  { label: 'Logistique Europe',     icon: 'fa-truck',        color: '#2196F3' },
  logistics_africa:  { label: 'Logistique Africa',     icon: 'fa-globe-africa', color: '#FF9800' },
  virtual_mailbox:   { label: 'Virtual Mailbox',       icon: 'fa-mailbox',      color: '#673AB7' },
  hr:                { label: 'RH & Personnel',        icon: 'fa-user-tie',     color: '#9C27B0' },
  it:                { label: 'IT Service',             icon: 'fa-laptop-code',  color: '#00BCD4' },
  finance:           { label: 'Finance',                icon: 'fa-chart-line',   color: '#4CAF50' },
  customer_service:  { label: 'Customer Service',       icon: 'fa-headset',      color: '#E91E63' },
  marketing:         { label: 'Marketing',              icon: 'fa-bullhorn',     color: '#607D8B' },
  knowledge:         { label: 'Base de connaissances',  icon: 'fa-book',         color: '#795548' },
  monitoring:        { label: 'Monitoring',             icon: 'fa-chart-bar',    color: '#FF5722' },
  crm:               { label: 'CRM',                    icon: 'fa-handshake',    color: '#1565C0' },
  admin:             { label: 'Administration',         icon: 'fa-shield-alt',   color: '#F44336' },
};

// ── Auto-derived RBAC sub-modules from NAV_SECTIONS ──
// Adding a nav section with a `module` field automatically creates RBAC entries.

/** Converts a route string to a subModule key: 'finance/transactions' → 'finance_transactions' */
function routeToKey(route: string): string {
  return route.replace(/\//g, '_').replace(/-/g, '_');
}

/**
 * Auto-derived from NAV_SECTIONS.
 * Every NavSection with a `module` field generates RBAC sub-modules from its children.
 */
export const RBAC_SUBMODULES: Record<RbacModule, { key: string; label: string }[]> = (() => {
  const result: Record<string, { key: string; label: string }[]> = {};
  // Initialize all modules with empty arrays
  for (const mod of Object.keys(RBAC_MODULE_CONFIG)) {
    result[mod] = [];
  }
  // Populate from NAV_SECTIONS
  for (const section of NAV_SECTIONS) {
    if (section.module && result[section.module] !== undefined) {
      for (const child of section.children) {
        result[section.module].push({
          key: routeToKey(child.route),
          label: child.label,
        });
      }
    }
  }
  // #935 — sous-module SYNTHÉTIQUE (aucun enfant nav propre) : l'ÉDITION du
  // registre des livreurs (`partners/new`, `partners/:id`) porte un jeu de
  // rôles plus restrictif que la liste `partners` (#921, PARTNER_REGISTRY_ROLES)
  // et la résolution par préfixe ne sait pas les distinguer. Les deux routes
  // portent le mapping explicite en `data.rbac` ; l'entrée ici fait exister le
  // sous-module dans la matrice (écran rbac-settings) et dans le seed
  // (`seedLegacyAclRules` + `SUBMODULE_SEED_ROLES.partners_registry`).
  result['logistics_africa'].push({ key: 'partners_registry', label: 'Édition registre livreurs' });
  return result as Record<RbacModule, { key: string; label: string }[]>;
})();

// ── CRUD Operations ──

export type CrudOperation = 'read' | 'create' | 'update' | 'delete';

export interface AclOperations {
  read: boolean;
  create: boolean;
  update: boolean;
  delete: boolean;
}

export const EMPTY_ACL_OPS: AclOperations = { read: false, create: false, update: false, delete: false };
export const FULL_ACL_OPS: AclOperations = { read: true, create: true, update: true, delete: true };

// ── Role ──

export interface RbacRole {
  id: string;
  name: string;           // clé unique (ex: 'hr_admin')
  label: string;          // nom affiché (ex: 'Administrateur RH')
  description: string;
  isSystem: boolean;      // true = ne peut pas être supprimé
  createdAt: Date;
  updatedAt: Date;
}

// Rôles système par défaut (mapping depuis le champ `role` existant sur employees)
export const SYSTEM_ROLES: { name: string; label: string; description: string }[] = [
  { name: 'super_admin', label: 'Super Administrateur', description: 'Accès total, bypass toutes les règles ACL' },
  { name: 'admin',       label: 'Administrateur',       description: 'Gestion complète du backoffice' },
  { name: 'rh',          label: 'Ressources Humaines',  description: 'Gestion RH et personnel' },
  { name: 'manager',     label: 'Manager',              description: 'Supervision opérationnelle' },
  { name: 'topManager',  label: 'Top Manager',          description: 'Direction générale' },
  { name: 'warehouse',   label: 'Entrepôt',             description: 'Opérations entrepôt (réception, tri, expédition)' },
  { name: 'transitory',  label: 'Transitaire',          description: 'Gestion douanière et transit' },
  // #1819 — personnel de Kinshasa : réception, pesée, dégroupage, tri et livraison au Congo ; ni Europe, ni finance.
  { name: 'delivery',    label: 'Logistique RDC',       description: 'Réception, pesée, dégroupage, tri et livraisons au Congo (ni Europe, ni finance)' },
  { name: 'customs',     label: 'Douanes',              description: 'Formalités douanières' },
  // #2050 — agent du service client : tickets clients et base de connaissances, ni RH ni finance.
  { name: 'support',     label: 'Service client',       description: 'Tickets clients et base de connaissances (ni RH, ni finance)' },
];

// ── Group ──

export interface RbacGroup {
  id: string;
  name: string;
  description: string;
  memberIds: string[];     // IDs employés
  roleIds: string[];       // IDs rôles assignés au groupe
  managerId?: string;
  managerName?: string;
  location?: string;       // site (ex: 'Tubize')
  department?: string;     // département lié (ex: 'logistique')
  parentGroupId?: string;  // groupe parent (héritage hiérarchique)
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
}

// Groupes système par défaut — hiérarchie parent → enfant
export interface SeedGroup {
  name: string;
  description: string;
  department?: string;
  location?: string;
  parentName?: string;
}

export const SEED_GROUPS: SeedGroup[] = [
  // ── Groupes parents (départements transversaux) ──
  { name: 'Direction',           description: 'Direction générale — tous sites',               department: 'direction' },
  { name: 'Logistique',         description: 'Département logistique — tous sites',            department: 'logistique' },
  { name: 'Support',            description: 'Département support client — tous sites',        department: 'support' },
  { name: 'Finance',            description: 'Département finance — tous sites',               department: 'finance' },
  { name: 'Ressources Humaines', description: 'Équipe RH — gestion du personnel',             department: 'direction' },

  // ── Groupes enfants (logistique par site) ──
  { name: 'Logistique Tubize',     description: 'Équipe logistique — entrepôt Tubize',         department: 'logistique', location: 'Tubize',     parentName: 'Logistique' },
  { name: 'Logistique Kinshasa',   description: 'Équipe logistique — site Kinshasa',           department: 'logistique', location: 'Kinshasa',   parentName: 'Logistique' },
  { name: 'Logistique Lubumbashi', description: 'Équipe logistique — site Lubumbashi',         department: 'logistique', location: 'Lubumbashi', parentName: 'Logistique' },

  // ── Groupes enfants (support par site) ──
  { name: 'Support Tubize',        description: 'Équipe support client — Tubize',              department: 'support',    location: 'Tubize',     parentName: 'Support' },
  { name: 'Support Kinshasa',      description: 'Équipe support client — Kinshasa',            department: 'support',    location: 'Kinshasa',   parentName: 'Support' },
];

// ── ACL Rule ──

export interface RbacAclRule {
  id: string;
  module: RbacModule;
  subModule: string;       // clé du sous-module (ou '*' pour tout le module)
  operations: AclOperations;
  roleId: string;          // rôle concerné
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
}

// ── Runtime: permissions effectives pour l'utilisateur courant ──

export interface EffectivePermissions {
  roles: string[];               // tous les role IDs (directs + hérités des groupes)
  groups: string[];              // tous les group IDs
  acl: Map<string, AclOperations>; // clé = "module:subModule" → ops
}

// ── Route → RBAC mapping (auto-derived from NAV_SECTIONS) ──

export const ROUTE_TO_RBAC: Record<string, { module: RbacModule; subModule: string }> = (() => {
  const result: Record<string, { module: RbacModule; subModule: string }> = {};
  for (const section of NAV_SECTIONS) {
    if (!section.module) continue;
    for (const child of section.children) {
      result[child.route] = {
        module: section.module,
        subModule: routeToKey(child.route),
      };
    }
  }
  return result;
})();

/**
 * #813 — résout le mapping matrice d'un chemin de route backoffice.
 * Match exact dans ROUTE_TO_RBAC, sinon par préfixe en retirant les segments
 * de fin ('containers/:id' → 'containers', 'tickets/:id/edit' → 'tickets').
 * Retourne null si la route n'est portée par aucune entrée nav — le guard
 * retombe alors sur les data.roles legacy (jamais de lockout), sauf mapping
 * explicite posé dans `data.rbac` (routes hors nav : heavy-processing, team…).
 */
export function resolveRouteRbacMapping(path: string): { module: RbacModule; subModule: string } | null {
  const segments = path.split('/');
  for (let len = segments.length; len >= 1; len--) {
    const hit = ROUTE_TO_RBAC[segments.slice(0, len).join('/')];
    if (hit) return hit;
  }
  return null;
}

/**
 * #935 — VERBE requis pour accéder à une route (convention arbitrée le 04/08).
 *
 * Avant, `PermissionService.routeAllowed` consultait `read` pour TOUTES les
 * routes : 47 écrans d'ÉCRITURE (formulaires de création/édition) étaient
 * décidés sur un droit de lecture. Latent tant que le seed est tout-`FULL`,
 * faux dès qu'un admin affine la matrice : retirer « créer » à un rôle en lui
 * laissant « lire » n'aurait pas fermé le formulaire.
 *
 * Convention (mécanique, pas de débat écran par écran) :
 *   - segment terminal `new`  → `create` ;
 *   - segment terminal `edit` → `update` (toutes les routes `…/:id/edit`) ;
 *   - route d'édition terminée par `:id` sans suffixe → `update`, via la liste
 *     explicite ci-dessous (un `:id` nu est ambigu : les vues DÉTAIL restent
 *     en lecture) ;
 *   - tout le reste → `read` (listes, détails, tableaux de bord).
 *
 * La liste est verrouillée par un test anti-pourrissement dérivé des VRAIES
 * routes (`backoffice-route-verbs.spec.ts`) : un nouveau `*FormComponent`
 * routé sur `:id` sans entrée ici fait rougir la CI.
 */
export const ID_EDIT_FORM_ROUTES = new Set<string>([
  'partners/:id',
  'directory/:id',
  'team/:id',
  'shipping-partners/:id',
  'logistics-team/:id',
  'promotions/:id',
  'marketing-partners/:id',
  'campaigns/:id',
]);

export function requiredRouteOperation(path: string): CrudOperation {
  const last = path.split('/').pop();
  if (last === 'new') return 'create';
  if (last === 'edit') return 'update';
  if (ID_EDIT_FORM_ROUTES.has(path)) return 'update';
  return 'read';
}

// ── Sidebar nav key → RBAC module mapping (auto-derived) ──

export const NAV_KEY_TO_MODULE: Record<string, RbacModule> = (() => {
  const result: Record<string, RbacModule> = {};
  for (const section of NAV_SECTIONS) {
    if (section.module) {
      result[section.key] = section.module;
    }
  }
  return result;
})();

// ══════════════════════════════════════════
// #814 — retours des Cloud Functions d'administration RBAC
// ══════════════════════════════════════════
//
// Miroirs des types serveur (functions/src/functions/admin/assign-employee-role.ts
// et propagate-rbac-claims.ts). Pas de package partagé front/functions dans ce
// repo : si l'un des deux change, changer l'autre.

/** Retour de la CF `assignEmployeeRole`. */
export interface AssignEmployeeRoleResult {
  success: boolean;
  employeeId: string;
  authUid: string;
  /** Flag `app_config/main.rbac.enabled` au moment de l'appel. */
  rbacEnabled: boolean;
  /** `false` = employé sans compte Auth (invitation non consommée) : les claims
   *  seront posées à la prochaine propagation, l'écriture Firestore est faite. */
  claimsApplied: boolean;
  /** Modules effectivement portés par les claims après recalcul. */
  modules: string[];
  claimsError?: string;
}

/** Retour de la CF `propagateRbacClaims`. */
export interface PropagateRbacClaimsResult {
  success: boolean;
  rbacEnabled: boolean;
  processed: number;
  updated: number;
  unchanged: number;
  /** Employés sans compte Auth : normal tant qu'une invitation n'est pas consommée. */
  skippedNoAuth: number;
  degraded: number;
  errors: number;
  /** `true` si la propagation a été arrêtée au plafond de sécurité (5000). */
  truncated: boolean;
  dryRun: boolean;
  durationMs: number;
}
